// ================= THE CARD TILE =================
//
// ONE RENDERER FOR A CARD'S COMPACT FACE, and the reason it is its own file is that a second one
// is about to be tempting. The deck builder (deck_builder/implementation-plan.md) shows every card
// in the legal pool and every card in the draft, which is the same tile the Select rack and the
// Telegraph already draw -- and it is not the game shell, so it cannot load ui.js to get it. The
// two ways out of that are "extract the renderer" and "write a second one for the builder", and the
// second one drifts: the day a stat moves on the tile, the builder goes on showing the old face and
// nothing fails, because there is no assertion anywhere that two renderers agree.
//
// So this is pure motion out of ui.js's IIFE. Every function here was defined there and is called
// from there still, through thin delegating consts -- no call site changed, and `compactCardHtml`
// remains the single implementation. What moved is the transitive closure of what the tile reads:
// the ownership palette, the sigils, the Speed meter, the stat glyphs, the cost pips and the
// conditions strip. Helpers that only OTHER surfaces use (ownershipTagsHtml, familyMotifHtml,
// staminaPips, statFigureHtml, guardPipsHtml, the expanded panels) stayed behind.
//
// AND THEN THEY DID NOT, which is worth recording here because this header is where the next
// person reads the boundary. Every one of those names is in js/card-detail.js now. The reason is
// the one two paragraphs up, arriving on schedule: library.html grew a card sheet, could reach
// only this file, rendered the COMPACT tile into it -- and became the one screen in the game
// where a card's Conditions could not be read properly, while the bout and the Setup preview
// showed the expanded drawer. That is the second renderer this file exists to prevent, wearing
// the shape nobody was watching for. See js/card-detail.js's header and
// deploy_handoff/library-card-sheet-plan.md.
//
// Loads after cards.js (the colour tables and STYLE_LABELS are read at init) and before ui.js.
// Everything else it needs -- window.CARDS, window.DECK_META, window.UISilhouette, window.ENGINE --
// is read at CALL time, so this file has no opinion about where those scripts sit.
(function () {
  'use strict';

  function escapeHtml(str) {
    return String(str).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
  }

  // ---------------- Ownership / role visual language ----------------
  // The tables live in js/cards.js: the fighter HUD's neon and Zone 3's beat rows read the same
  // palette now, and three copies of a colour table is two too many.
  const FAMILY_COLORS = window.FAMILY_COLORS;
  const STYLE_COLORS = window.STYLE_COLORS;

  // Read from DeckRules at CALL time and never copied into a literal here. js/deck-rules.js loads
  // after this file in index.html, and not at all on the art prototype page -- and a local
  // `'Challenge'` string would be the second definition of a value whose whole job is to be the
  // only one. With DeckRules absent this returns undefined, which no real card's tier equals, so
  // the branch below simply does not fire and the colour is what it was before this existed.
  function challengeTier() {
    return window.DeckRules && window.DeckRules.CHALLENGE_TIER;
  }
  // A Challenge card gets a frame of its own, and this is the ONE surface where such a card is ever
  // drawn: it is absent from the builder's pool and from every deck listing by rule, so the first
  // time a player sees it is the telegraph in an exchange. Without this branch it fell through to
  // the Style line and borrowed whichever colour its `ownershipGroup` happened to name -- a card
  // nobody can own, wearing the frame of a Style they can, at the one moment it is on screen.
  //
  // Written before the first Challenge card exists, for the same reason DeckRules.CHALLENGE_TIER
  // was: the default was not a crash, it was a plausible-looking wrong answer.
  function ownershipColor(card) {
    if (card.ownershipTier === 'Universal') return 'var(--universal)';
    if (card.ownershipTier === 'Family') return FAMILY_COLORS[card.ownershipGroup] || 'var(--universal)';
    if (card.ownershipTier === challengeTier()) return 'var(--challenge)';
    return STYLE_COLORS[card.ownershipGroup] || 'var(--universal)';
  }
  // ---------------- The ownership tier's PLAYER-FACING name ----------------
  //
  // "FAMILY" MEANT TWO DIFFERENT THINGS AND THE COLLISION WAS REAL. It is the ownership tier of a
  // card shared by two Styles (Torque & Pressure, Fulcrum & Flow), and it was also the ruleset's
  // word for an Attack Type (Punch/Kick/Grapple) and for a Defense Type (Absorb/Deny/Dodge/Deflect).
  // Three different taxonomies, one word, all three on screen at once on a card tile. The
  // mechanics-visibility brief lists resolving it as a deliverable and this is the resolution:
  // the combat layers are now Attack Type, Follow-through and Defense Type
  // (js/mechanics-dictionary.js), and the ownership tier is Foundation.
  //
  // "Foundation" over the brief's other suggestion, "Card Package": one word rather than two, so it
  // fits the ribbon this label is printed in ("Foundation · Muay Thai"), and it reads as what the
  // tier actually is -- the cards a Style is built on and shares with its sibling.
  //
  // DISPLAY ONLY, AND THAT IS THE WHOLE OF IT. `ownershipTier === 'Family'` stays the identifier in
  // card data, deck-rules, the validator, every saved deck and every share code. Renaming the data
  // would invalidate stored decks for a word; renaming the label costs nothing and is what the
  // player actually reads.
  const TIER_LABEL = { Family: 'Foundation' };
  function tierLabel(tier) { return TIER_LABEL[tier] || tier; }

  // The Style still travels in the label. It is not decoration: DECK_META[id].style drives the
  // automatic Style passive the deck holding this card will be collecting, so "Challenge · Muay
  // Thai" tells a player which passive is in play across the table from them.
  function ownershipLabel(card) {
    const label = window.STYLE_LABELS[card.style] || card.style;
    return card.ownershipTier === 'Universal' ? label : `${tierLabel(card.ownershipTier)} · ${label}`;
  }

  // ---------------- Visual language: style sigil ----------------
  // Small abstract glyphs — "structure, rhythm, or force, never a fighter" — one per Style,
  // matching the verbal descriptions in the visual-language design doc's Style Atlas. Only
  // Style-tier cards show a sigil (Family cards are shared by two Styles, so no single sigil
  // applies to them; Universal cards stay plain per the ownership-frame rule already in place).
  const STYLE_SIGILS = {
    ShotokanKarate: '<svg viewBox="0 0 24 24" fill="none"><line x1="3" y1="8" x2="14" y2="8" stroke="currentColor" stroke-width="1.6"/><line x1="3" y1="16" x2="14" y2="16" stroke="currentColor" stroke-width="1.6"/><path d="M12 4 L21 12 L12 20" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linejoin="round"/></svg>',
    Taekwondo: '<svg viewBox="0 0 24 24" fill="none"><path d="M13 20 C4 20 4 9 11 7.5 C16 6.3 18 10 14.5 12.3" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/><circle cx="14.5" cy="12.3" r="1.5" fill="currentColor"/></svg>',
    WesternBoxing: '<svg viewBox="0 0 24 24" fill="none"><circle cx="9" cy="9" r="5" stroke="currentColor" stroke-width="1.6"/><circle cx="15" cy="15" r="5" stroke="currentColor" stroke-width="1.6"/></svg>',
    MuayThai: '<svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.6"/><line x1="12" y1="1" x2="12" y2="5" stroke="currentColor" stroke-width="1.6"/><line x1="12" y1="19" x2="12" y2="23" stroke="currentColor" stroke-width="1.6"/><line x1="1" y1="12" x2="5" y2="12" stroke="currentColor" stroke-width="1.6"/><line x1="19" y1="12" x2="23" y2="12" stroke="currentColor" stroke-width="1.6"/><line x1="4.2" y1="4.2" x2="7" y2="7" stroke="currentColor" stroke-width="1.6"/><line x1="17" y1="17" x2="19.8" y2="19.8" stroke="currentColor" stroke-width="1.6"/><line x1="19.8" y1="4.2" x2="17" y2="7" stroke="currentColor" stroke-width="1.6"/><line x1="7" y1="17" x2="4.2" y2="19.8" stroke="currentColor" stroke-width="1.6"/></svg>',
    WingChun: '<svg viewBox="0 0 24 24" fill="none"><line x1="6" y1="4" x2="18" y2="20" stroke="currentColor" stroke-width="1.6"/><line x1="18" y1="4" x2="6" y2="20" stroke="currentColor" stroke-width="1.6"/></svg>',
    PencakSilat: '<svg viewBox="0 0 24 24" fill="none"><path d="M3 20 L3 15 L9 15 L9 10 L15 10 L15 5 L21 5" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linejoin="round"/></svg>',
    Wrestling: '<svg viewBox="0 0 24 24" fill="none"><path d="M4 6 L12 15 L20 6" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linejoin="round" stroke-linecap="round"/><line x1="4" y1="20" x2="20" y2="20" stroke="currentColor" stroke-width="1.6"/></svg>',
    Judo: '<svg viewBox="0 0 24 24" fill="none"><circle cx="11" cy="13" r="8" stroke="currentColor" stroke-width="1.6"/><circle cx="17" cy="6.5" r="1.7" fill="currentColor"/><line x1="11" y1="13" x2="17" y2="6.5" stroke="currentColor" stroke-width="1.3"/></svg>'
  };

  function styleSigilHtml(card) {
    if (card.ownershipTier !== 'Style') return '';
    const sigil = STYLE_SIGILS[card.ownershipGroup];
    return sigil ? `<span class="card-sigil" style="color:${ownershipColor(card)}">${sigil}</span>` : '';
  }

  function moveGlossHtml(card, className) {
    return card.moveGloss ? `<div class="${className || 'ribbon-gloss'}">${escapeHtml(card.moveGloss)}</div>` : '';
  }

  // ---------------- The technique window ----------------
  //
  // A bounded panel holding the card's flavour line and a figure of the move being thrown. It does
  // not ADD a row: it takes over the gloss's row and puts the gloss inside itself, so the whole cost
  // of the art is the panel's own min-height rather than a new band of tile.
  //
  // OPT-IN, and not because the markup is expensive. Two surfaces cannot afford it and say so in
  // their own CSS already: `.ct-pair`'s micro-planks hide the gloss outright, and the Telegraph rail
  // clamps it to one line inside ~140px. Rendering the window there and hiding it with CSS would
  // work, but it would put a lazy-loading <img> for every card into two surfaces that will never
  // paint it — so the surfaces that want it ask, and the ones that do not are unchanged, markup and
  // all.
  //
  // WHOSE FIGURE. The card's OWNER throws it, so the actor is `opts.fighter`, which is the opposite
  // body from the one the Attack silhouette is drawn on. See MoveArt.artKeyFor.
  //
  // Degrades in three steps rather than failing: no MoveArt module -> the plain gloss, exactly as
  // before; no art for this fighter -> the SVG pose; no gloss on the card -> a window with only the
  // figure, which is still the card saying what its move looks like.
  function techniqueWindowHtml(card, opts) {
    if (!window.MoveArt) return moveGlossHtml(card);
    const art = window.MoveArt.glyphHtml(card, { artKey: window.MoveArt.artKeyFor(opts.fighter) });
    return `<div class="cc-window">${art}${moveGlossHtml(card)}</div>`;
  }

  // ---------------- Small shared visual helpers ----------------
  //
  // L / P / C: which bands this attack is Optimal in. `currentRange` is optional and, when given,
  // marks the band the fight is standing at right now and grows the row's title into the sentence
  // §5 actually makes -- "Optimal at Pocket. You are at Long: one band off, -2 Power and no
  // clean-hit effects."
  //
  // WHY IT WAS WORTH THREADING A PARAMETER FOR. The three letters were a fact about the CARD, and
  // range effectiveness is a comparison between that fact and where the exchange is being fought.
  // A player holding a Clinch-only knee at Long could read both halves off the screen and was never
  // shown the subtraction, which is the part that decides the exchange.
  //
  // WITH NO currentRange THE OUTPUT IS BYTE-IDENTICAL to what it always was, and a test asserts it.
  // This renderer is shared with the deck builder, the card library and the Home screen, where
  // there is no fight and no current range -- a marker there would be a claim about a bout that is
  // not happening.
  //
  // Composed from ENGINE, never restated: rangeEffectiveness names the band and bandDistance gives
  // the number, so the ladder has one implementation and this has none. Guarded on ENGINE being
  // present because js/card-tile.js is loaded by deck-builder.html and library.html, and neither
  // ships the engine -- there the parameter simply cannot be passed.
  function rangeBadgeRow(optimalRanges, currentRange) {
    const all = ['Long', 'Pocket', 'Clinch'];
    const isAll = optimalRanges === 'All';
    const E = window.ENGINE;
    const live = (currentRange && E && E.rangeEffectiveness) ? currentRange : null;
    const fit = live ? E.rangeEffectiveness(optimalRanges, live) : null;
    const rowTitle = live ? ` title="${escapeHtml(fitSentence(optimalRanges, live, fit, E))}"` : '';
    return `<div class="badge-row"${rowTitle}>` + all.map(r => {
      const on = isAll || (Array.isArray(optimalRanges) && optimalRanges.includes(r));
      const here = live === r ? ' here fit-' + fit.toLowerCase() : '';
      return `<div class="badge ${on ? 'on' : ''}${here}" title="${r}">${r[0]}</div>`;
    }).join('') + `</div>`;
  }

  // The sentence the row carries when it knows where the fight is. Reads as one thought rather than
  // two clauses joined by a dash, because it is answering a question the player is holding in their
  // head ("can I use this right now?") and not labelling a widget.
  function fitSentence(optimalRanges, currentRange, fit, E) {
    const wants = optimalRanges === 'All' ? 'any range' : 'at ' + [].concat(optimalRanges).join(' or ');
    if (fit === 'Optimal') return `Optimal ${wants}. You are at ${currentRange} — full effect.`;
    const bands = E.bandDistance(optimalRanges, currentRange);
    const cost = fit === 'Whiff'
      ? 'the attack does nothing at all'
      : '-2 Power and no clean-hit effects';
    return `Optimal ${wants}. You are at ${currentRange} — ${bands} band${bands === 1 ? '' : 's'} ` +
           `off: ${cost}.`;
  }
  // (levelBadgeRow is gone. Three lettered squares were the old way of saying where a card lands;
  // every surface draws the banded silhouette now — see panelSilhouetteHtml and js/ui-silhouette.js.
  // rangeBadgeRow stays: L/P/C is a distance, not a target zone, and has no figure to draw on.)

  // WHOSE BODY IS THE DIAGRAM DRAWN ON. Both figures used to be the card OWNER's, on the reading
  // that a card in your hand belongs to you and should be drawn on you. That is right for the
  // Defense half and wrong for the Attack half, and the difference is not decoration:
  //
  //   DEFENSE bands say WHAT YOU COVER. Your head, your ribs, your legs — your body.
  //   ATTACK bands say WHERE THIS LANDS. Not on you. On the person in front of you.
  //
  // Drawn on the same figure, the two halves of a card read as one fighter doing two things to
  // himself, and the exchange the player is actually being asked to picture — my attack against
  // their guard — is nowhere on the card. So the Attack half takes the OPPONENT's figure and the
  // Defense half keeps the owner's, on every surface: in your hand, on the Telegraph (where their
  // attack is drawn on YOU), and on the Commit card.
  //
  // `figureFor` still answers "which figure is this fighter", because the question it is asked has
  // moved to the call sites, which are the only places that know who is opposite whom. The one
  // caller with no opponent at all — the setup deck browser, where there is no bout — passes the
  // same figure twice, which is the old behaviour and the only sensible reading there.
  //
  // TWO WAYS TO NAME A FIGURE, and `artKey` wins. A fighter in a bout has a `deckId` and the figure
  // follows the deck. The deck BUILDER has no deck yet — it is choosing a figure directly — and used
  // to say so by passing a bare `{ deckId: someArtKey }`, which worked only while every art key
  // happened to also be a deck id. Once art exists that no deck wears, that trick resolves to null
  // and the tile renders figureless without erroring. So the builder passes `artKey` and means it.
  function figureFor(fighter) {
    if (!fighter) return null;
    if (fighter.artKey) {
      const art = window.FIGHTER_ART && window.FIGHTER_ART[fighter.artKey];
      if (art && art.rangeFigure) return art.rangeFigure;
    }
    const m = fighter.deckId && window.DECK_META && window.DECK_META[fighter.deckId];
    return (m && m.rangeFigure) || null;
  }

  // ---------------- The Speed meter ----------------
  // Fast is a LOW tempo, so a fast card gets THREE pips -- see window.SPEED_TIERS for why that cut
  // lives in cards.js and not here.
  const STOPWATCH = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<line x1="9.5" y1="2.5" x2="14.5" y2="2.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
    '<circle cx="12" cy="13.5" r="7.5" stroke="currentColor" stroke-width="1.8"/>' +
    '<path d="M12 9.5 L12 13.5 L14.5 15.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';
  // The Speed Edition's own words (rules SS7), not the display buckets they replace. 'Even' in
  // particular has to go: two Standard Attacks are not 'even', they resolve together.
  const SPEED_WORD = { 3: 'Fast', 2: 'Standard', 1: 'Committed' };
  // THE STOPWATCH AND THREE PIPS ARE THE SPEED UI. Not one of two forms — the only one. Every
  // surface that used to print `S3` as text now draws this instead: the rack tile's identity line,
  // the Select pair tray's slots, and the title row of all three drawer forms. A text code and a
  // meter saying the same number were a card naming its Speed twice, and the code was doing it in
  // the slot a reader takes for the card's IDENTITY — which is what made it read as a display ID
  // and then turn out not to be one.
  //
  // ONE HOME, AND IT IS THE TITLE ROW. The Commit card briefly carried a labelled variant in the
  // Attack panel's stat column, which is where the approved dashboard draws it — and it did not
  // fit: at 1366x768 that column went 57px -> 94px, four stats wrapping to three rows where three
  // wrapped to two, and the 37px came out of Conditions until one of the four panels rendered 10px
  // of a needed 74. The title row takes the meter for nothing, because it is the slot the `S3` text
  // was already occupying. A component that has to be hidden at half the supported viewports is not
  // the Speed UI; this one renders everywhere.
  //
  // `fighter` is optional and adds the one thing a printed value cannot say: a queued +/-1 from
  // last exchange, knowable now and decisive for who acts first. Marked beside the pips rather than
  // folded into them, because the pips are what the card PRINTS and a meter with a silent fourth
  // state would be misreporting it. Deliberately excludes the card's own `speedModIf`, which
  // resolves against a Clash range not locked until after Commit.
  function speedMeterHtml(card, fighter) {
    // AUTHORED, not derived. The pips used to be a display bucket computed from tempo via
    // window.speedPips(); they are now the printed Speed itself, and the two disagree on 14 of the
    // 152 cards — Cross Counter is the sharpest, authored Speed 1 against a tempo of 30 that the
    // old cut called Even. The engine resolves by this number, so the face has to show this number.
    const n = window.ENGINE.printedSpeedOf(card);
    const final = fighter ? window.ENGINE.computeFinalSpeed(fighter, card, []) : n;
    // No tiebreak number to fall back on any more: three pips is the whole of it, and equal pips
    // means resolving together rather than "whoever is nearer the edge of the bucket".
    const title = final === n
      ? `${SPEED_WORD[n]} — Speed ${n}, ${n === 3 ? 'resolves first' : n === 1 ? 'resolves last' : 'resolves after Fast'}. Higher acts first.`
      : `Printed Speed ${n} (${SPEED_WORD[n]}), now ${final} (${SPEED_WORD[final]}) from a queued ` +
        `change last exchange. Higher acts first.`;
    let pips = '';
    for (let i = 0; i < 3; i++) pips += `<i class="${i < n ? 'on' : ''}"></i>`;
    const queued = final !== n
      ? `<em class="speed-mod">${final > n ? '+' : ''}${final - n}</em>` : '';
    return `<span class="cc-speed" data-speed="${n}" title="${escapeHtml(title)}">
      ${STOPWATCH}<span class="cc-pips" aria-hidden="true">${pips}</span>${queued}
      <span class="visually-hidden">${escapeHtml(title)}</span>
    </span>`;
  }

  // ---------------- Stat iconography ----------------
  // The approved Commit gives Power and Guard a GLYPH rather than a ring with the word inside it —
  // an impact burst and a shield (deploy_handoff/commit-card-gap.md §D). Two reasons it is worth
  // the change beyond matching the mockup: the two stats were previously distinguishable only by
  // their label and their tint, which is the weakest possible pairing for anyone reading quickly
  // or reading without colour; and the ring's 40px circle spent its area on empty middle while
  // the numeral inside it sat at 1.05rem.
  //
  // The WORD STAYS. It moves out from inside the ring to beside the numeral, but it does not go:
  // an icon is a second channel, not a replacement for the first, and "the shape means Guard" is
  // exactly the kind of thing a player has to be taught rather than told.
  const POWER_BURST = '<svg viewBox="0 0 24 24" aria-hidden="true" class="stat-glyph">' +
    '<path d="M12 1.6 L14.6 7.4 L20.6 5.2 L18.1 11 L23.4 13.4 L17.6 15 L19.4 21 L14 17.9 ' +
    'L12 23.2 L10 17.9 L4.6 21 L6.4 15 L0.6 13.4 L5.9 11 L3.4 5.2 L9.4 7.4 Z" ' +
    'fill="currentColor" fill-opacity="0.22" stroke="currentColor" stroke-width="1.3" ' +
    'stroke-linejoin="round"/></svg>';
  const GUARD_SHIELD = '<svg viewBox="0 0 24 24" aria-hidden="true" class="stat-glyph">' +
    '<path d="M12 2.2 L20.5 5.4 V12 C20.5 16.8 16.8 20.4 12 21.9 C7.2 20.4 3.5 16.8 3.5 12 ' +
    'V5.4 Z" fill="currentColor" fill-opacity="0.18" stroke="currentColor" stroke-width="1.5" ' +
    'stroke-linejoin="round"/></svg>';

  // Power for the five multi-strike attacks. Every one of them has UNIFORM per-beat power
  // (5x1, 3x2, 2x2, 2x2, 2x3), so `n x P` is both shorter than a total and strictly more
  // informative: the beat count is the thing that makes these cards different, and a total of 5
  // hides whether that is one heavy strike or five light ones.
  function attackPowerHtml(a) {
    if (!a.strikes) return `<b>${a.power}</b>`;
    const p = a.strikes[0].power;
    const uniform = a.strikes.every(s => s.power === p);
    return uniform
      ? `<b class="is-multi">${a.strikes.length}<em>&times;</em>${p}</b>`
      : `<b class="is-multi">${a.strikes.reduce((t, s) => t + (s.power || 0), 0)}</b>`;
  }

  // STAMINA COST, ON THE ONLY SURFACE THAT DID NOT SAY IT. The expanded card has printed a cost
  // since the pips got their legend; the compact card printed Speed, Range, Power and Guard and
  // NOTHING about what the card costs to use — so on Select, the screen where the hand is compared
  // and two of five are kept, the one stat that spends a resource was the one stat missing. A
  // player could pick two cards and find out at Commit.
  //
  // ONLY WHEN THERE IS SOMETHING TO PAY. 139 of 194 cards are free on both halves, so a row that
  // always rendered would be blank four times out of five and would cost every tile in the rack its
  // height (the grid stretches them equal) to say "free" — which is what the absence already says.
  // The expanded card still prints FREE outright, because there the question is being asked.
  //
  // WHICH HALF, AND WHY THAT IS A SEPARATE CHANNEL. No card in the pool costs on both halves — 49
  // are attack-side, 6 defense-side, 139 free — so one row is always enough, but WHICH one it is
  // matters at Commit, where the halves are assigned. So the pips carry the accent that means
  // Stamina everywhere else in the app, and the role word carries the role tint the ATK/DEF keys
  // and the PWR/GRD numerals on this same tile have already taught. Two facts, two channels,
  // neither borrowing the other's.
  //
  // PIPS RATHER THAN A NUMERAL, for the reason guardPipsHtml gives: a count is comparable between
  // two tiles at a glance in a way two digits are not, and cost only ever runs to 2. The word COST
  // goes with them, because the note on staminaPips is the whole lesson here — four circles with no
  // legend taught a new player nothing, and a smaller cluster of circles would teach them less.
  function compactCostHtml(card) {
    const a = (card.attack && card.attack.cost) || 0;
    const d = (card.defense && card.defense.cost) || 0;
    if (!a && !d) return '';
    const role = a ? 'atk' : 'def';
    const n = a || d;
    const half = a ? 'Attack' : 'Defense';
    // ATK / DEF, not ATT / DEF: the two silhouette keys on this same tile have printed ATK and DEF
    // since the figures arrived, and a third spelling of the same word in the same 46px column is
    // a second vocabulary to learn for nothing.
    const key = a ? 'ATK' : 'DEF';
    const title = `${n} Stamina to use the ${half} half of this card`;
    let pips = '';
    for (let i = 0; i < n; i++) pips += '<i></i>';
    return `<span class="cc-num cost ${role}" title="${escapeHtml(title)}">` +
      `<span class="cc-cost-pips" aria-hidden="true">${pips}</span>` +
      `<span><em class="cc-cost-role">${key}</em> COST</span>` +
      `<span class="visually-hidden">${escapeHtml(title)}</span></span>`;
  }

  // ---------------- The card's own rules, stripped to what the face does not already say --------
  // What the badges, circles and pips already say, said again in words. Every card's rules text
  // opens with its own signature -- "Opt P/C • choose M or L • P4." or "M/L • G3." -- which is
  // exactly the range badges, the level badges, the Power or Guard circle and the cost pips
  // rendered immediately above it. For a new player that is the hardest part of the card to read
  // and none of it is new information.
  //
  // So the card shows the REMAINDER: the clause that is actually specific to this card. `Requires`
  // survives the strip because nothing else on the face carries it. The full text is untouched in
  // the data, and the compendium and the logs still print it whole.
  //
  // Structural rather than exact: the level clause has eight different shapes across the pool
  // ("H/M", "choose H or M at Commit", "M then H", "choose five H/M/L strikes"), so the pattern
  // matches the two bullet-separated fields between "Opt" and the power value rather than trying
  // to enumerate them. Verified against all 152 cards -- every one is recognised.
  const ATTACK_SIGNATURE = /^(?:Dirty\s*•\s*)?Opt\s+[^•]+•\s*[^•]+•\s*P\d+(?:\s*(?:each|×\s*\d+))?(?:\s*•\s*Cost\s*\d+)?\s*\.?\s*/;

  // VALUE-AWARE, and it has to be. A number in the text is only redundant when it is the number the
  // circle shows. Four cards print a CONDITIONAL Guard -- "M/L • G4 against Kicks or Takedowns; G2
  // otherwise" on a card whose `guard` is 2, backed by a real `guardByCategory` in the engine --
  // and a blind strip removed the G4, leaving "against Kicks or Takedowns; G2 otherwise" against a
  // circle reading 2. That is not a trimmed card, it is a card that no longer makes sense.
  //
  // So the signature is only removed where the printed value matches the data. Where it does not,
  // the level prefix still goes (the badges do show that) and the Guard clause stays whole.
  function conditionsText(card, side) {
    const raw = String((card[side] && card[side].text) || '');
    if (!raw) return '';
    let rest = raw, keep = '';
    if (side === 'attack') {
      const req = /^((?:Requires|Req)\b[^.]*\.)\s*/.exec(rest);
      if (req) { keep = req[1] + ' '; rest = rest.slice(req[0].length); }
      const m = ATTACK_SIGNATURE.exec(rest);
      if (m) {
        const printed = /•\s*P(\d+)/.exec(m[0]);
        const power = card.attack && card.attack.power;
        if (!printed || power == null || +printed[1] === power) rest = rest.slice(m[0].length);
      }
    } else {
      const m = /^((?:Choose\s+)?[^•]{1,40}•\s*)G(\d+)(\s*•\s*Cost\s*\d+)?\s*/.exec(rest);
      if (m) {
        const guard = card.defense && card.defense.guard;
        if (guard == null || +m[2] === guard) {
          rest = rest.slice(m[0].length);
          // A qualifier hanging off the Guard value -- "; cannot guard Low" -- is a restriction the
          // circle cannot show, so it stays and is re-punctuated into its own sentence.
          rest = rest.replace(/^[;,]\s*/, '').replace(/^\.\s*/, '');
          if (/^against\b/i.test(rest)) rest = 'Guard applies ' + rest;
        } else {
          // Conditional Guard: drop only the levels, keep the numbers.
          rest = rest.slice(m[1].length).replace(/^(G\d+)\s*•\s*Cost\s*\d+/, '$1');
        }
      }
    }
    const out = (keep + rest).trim();
    return out ? out.charAt(0).toUpperCase() + out.slice(1) : '';
  }

  // THE CARD'S OWN RULES, for a surface wide enough to print them. The rack cannot: five tiles share
  // one row inside a 1100px workspace, so a tile has ~213px and a rule wraps to three or four ragged
  // lines. A plank has the whole width and prints the same rule as one sentence.
  //
  // Quoted through `conditionsText`, which is what the expanded panels print, so the plank and the
  // drawer cannot drift into saying different things about the same card -- and so the signature
  // ("Opt P/C • H • P3.") is stripped here too, since the badges beside it already show all of it.
  //
  // A half with nothing left after the strip renders NO row rather than an empty one. That is not
  // hypothetical: centerline_entry's Defense half is exactly this case.
  function cardRulesHtml(card) {
    // The role class is written out rather than left to :first-child/:last-child. A card whose
    // Attack half strips to nothing renders the DEF row first, and a positional selector would then
    // paint a Defense rule in the Attack colour -- mislabelling the one thing the colour is for.
    const rows = [['atk', 'ATK', conditionsText(card, 'attack')],
                  ['def', 'DEF', conditionsText(card, 'defense')]]
      .filter(r => r[2])
      .map(r => `<span class="cc-rule ${r[0]}"><b>${r[1]}</b><span>${escapeHtml(r[2])}</span></span>`);
    return rows.length ? `<div class="cc-rules">${rows.join('')}</div>` : '';
  }

  // ================= COMPACT CARD =================
  // (The one-line `ribbonHtml` plank this replaced is gone. It had exactly two callers -- the
  // Select hand and the Telegraph -- and both now render through compactCardHtml, which is the
  // point: the opponent's pair is read in the SAME visual language the player just used to choose
  // their own, not in a second dialect that happens to list the same fields.)
  //
  // The Selection face. Everything you compare two cards on, on the card itself, so choosing does
  // not mean opening each one in turn:
  //
  //   name + id · origin tier · speed pips · legal ranges · Power · Guard · where it can land ·
  //   where it covers · one line of flavour
  //
  // Conditions stay in the expanded read view. They are the one field with no bounded size (the
  // longest is four lines), and they are what you read AFTER narrowing to a card, not what you
  // narrow with.
  //
  // opts: { fighter, opponent, selected, cycling, disabled, stateText, stateClass, tabindex, onclick, id,
  //         readonly, rules, moveArt }
  //
  // `opponent` is the fighter the ATTACK half is drawn on — see figureFor. Optional, and it falls
  // back to the owner's own figure, which is both the old behaviour and the right answer for the
  // one surface that has no opponent (the setup deck browser). Not asserted the way `art` is in the
  // panel renderers, because "no opponent" is a real state here rather than a stale call site.
  //
  // THE IDENTITY LINE IS THE NAME, AND ONLY THE NAME. It printed `card.id` verbatim once
  // ("high_guard", "calm_the_storm"), which put a developer handle second on the tile; that was
  // replaced by an `S3` code, which put the card's SPEED in the slot a reader takes for its
  // identity — and a code in that position reads as a display ID, which it was not. The card has a
  // name and that is identification enough. Speed is the stopwatch and pips in the stat row below,
  // where it is one thing said once; the raw slug lives on the tile's `title` for anyone
  // cross-referencing the compendium.
  //
  // THE STATE BADGE LEFT THE HEAD ROW, and that is a fix rather than a rearrangement. It sat there
  // with `margin-left: auto` beside a `nowrap` ownership tag, so on a selected card it took ~60px
  // off the tag — measured at 1366x768, "STYLE · WESTERN BOXING" ellipsised to "STYLE · WE…", five
  // readable characters. The head row now holds nothing but ownership and the badge rides the title
  // line, which has slack because the name wraps there anyway.
  // ================= THE COMPACT ROW =================
  //
  // One line per card, for SCANNING a collection rather than reading a hand.
  //
  // WHY A SECOND SHAPE AND NOT A SMALLER TILE. The tile is built to be read one at a time -- two
  // silhouettes, a move-art window, a gloss -- and it is about 110 DOM nodes. The Cards page mounts
  // every card the player can build with, which the sitewide audit measured at 194 tiles, 21,939
  // elements and a 9,760px page; on a phone the grid collapses to one wide card per row and the
  // page becomes a catalogue nobody can find anything in. Shrinking the tile does not fix that,
  // because the cost is the tile's contents rather than its width -- so this drops them. About 20
  // nodes, no silhouettes, no art: name, what it costs you to know, and nothing else.
  //
  // A REAL ENABLED BUTTON, unlike the tiles this page draws today. Those are `readonly` -- focus
  // skipped, aria-disabled -- because a tile in a grid had no action; the audit's finding was 194
  // disabled buttons presenting detailed content. A row's action is "open the details", which is a
  // real thing to do, so it earns real button semantics rather than being excused from them.
  //
  // Everything here is the same helper the tile uses. Two renderers drawing the same card from two
  // copies of "what a card's Power is" is the drift js/card-tile.js exists to prevent.
  function compactRowHtml(cardId, opts) {
    opts = opts || {};
    const card = window.CARDS[cardId];
    if (!card) return '';
    const a = card.attack, d = card.defense;
    const state = opts.stateText
      ? `<span class="crow-state ${escapeHtml(opts.stateClass || 'neutral')}">${escapeHtml(opts.stateText)}</span>`
      : '';
    // The accessible name says what the control DOES, because that is what a screen reader user is
    // choosing between -- twelve rows all reading "Probe Jab" say nothing about which is a button.
    return `
      <button type="button" class="crow" data-card-id="${escapeHtml(card.id)}"
              style="--frame:${ownershipColor(card)}"
              aria-label="${escapeHtml(card.name)} — open details"
              ${opts.onclick ? `onclick="${opts.onclick}"` : ''}>
        <span class="crow-main">
          ${styleSigilHtml(card)}
          <span class="crow-name">${escapeHtml(card.name)}</span>
          ${state}
        </span>
        <span class="crow-nums">
          ${speedMeterHtml(card)}
          ${rangeBadgeRow(a.optimalRanges, opts.currentRange)}
          <span class="cc-num atk" title="Attack Power">${POWER_BURST}${attackPowerHtml(a)}<span>PWR</span></span>
          <span class="cc-num def" title="Defense Guard">${GUARD_SHIELD}<b>${d.guard}</b><span>GRD</span></span>
        </span>
      </button>`;
  }

  // ---------------- Mechanics badges ----------------
  //
  // ATTACK TYPE AND DEFENSE TYPE, ON EVERY CARD A PLAYER CAN SEE. This is the brief's P0: the
  // mechanics that carry an exchange into the next one were readable only after the fact, in the
  // exact log, so a five-card hand could not be compared on them. Because this renderer is the ONE
  // compact face -- the reason it was extracted from ui.js in the first place -- putting them here
  // covers the hand, the selected-pair tray, the opponent telegraph, the library and the deck
  // builder in one move, and they cannot drift apart afterwards.
  //
  // INSIDE THE EXISTING ATK/DEF KEY, which is the whole reason this costs no card height. The brief
  // is explicit -- "Do not add card height to make room... or expose the badge within the existing
  // Attack and Defense headers" -- and the key was already a line of text nobody was reading for
  // information. It now carries the one fact that half the tile's own silhouette cannot.
  //
  // THE FOLLOW-THROUGH IS THE DROPPABLE ONE. `is-followthrough` exists so CSS can take it away at
  // narrow widths without touching the type beside it: the type is what a player compares cards on,
  // the follow-through is what they read once they have chosen. Same sacrifice order, and the same
  // reasoning, as the commit panel's `is-continuation` token.
  //
  // Degrades to exactly the old markup where the dictionary is absent (the art prototype page) or
  // where a card has no type at all -- 16 of 211 do, the feints and trapping and dirty-trick cards,
  // which genuinely have no Attack Type in the ruleset. A blank is the truth there; "undefined" is
  // what this guard is preventing.
  // CHAMBER'S BADGE (RULES.md §19), and it is the only SUBSYSTEM badge in a row that is otherwise
  // pure taxonomy. That is a deliberate exception rather than the start of a pattern, and the
  // reason is arithmetic: eight cards carry Chamber, so a badge makes the whole set scannable in a
  // grid of 223. The other things a player wants to spot this way -- Off-Balance on 45 cards,
  // Openings on 49, Flow or Fury on 62 -- would put a badge on a quarter to a third of the pool
  // and the row would stop meaning anything. Those are the Library's mechanic FILTER's job; see
  // js/library-ui.js. A future mechanic earns a badge here by being rare, not by being important.
  //
  // The word differs by what the half does -- RELEASE spends, CHAMBER stores -- and both open the
  // same glossary entry, because they are one mechanic at two moments.
  function chamberBadge(card, role) {
    const M = window.MechanicsDictionary;
    const entry = M && M.get('chamber', 'Chamber');
    if (!entry) return '';
    const half = role === 'defense' ? card.defense : card.attack;
    if (!half) return '';
    if (role !== 'defense' && half.release) {
      return M.badgeHtml(Object.assign({}, entry, { label: 'RELEASE', shortLabel: 'RLS' }),
        { extraClass: 'is-tile is-release' });
    }
    return half.chamber ? M.badgeHtml(entry, { extraClass: 'is-tile is-chamber' }) : '';
  }

  function mechBadges(card, role) {
    const M = window.MechanicsDictionary;
    if (!M) return '';
    if (role === 'defense') {
      return M.badgeHtml(M.defenseTypeOf(card), { extraClass: 'is-tile is-defensetype' }) +
             chamberBadge(card, 'defense');
    }
    return M.badgeHtml(M.attackTypeOf(card), { extraClass: 'is-tile is-attacktype' }) +
           M.badgeHtml(M.followThroughOf(card), { extraClass: 'is-tile is-followthrough' }) +
           chamberBadge(card, 'attack');
  }

  function compactCardHtml(cardId, opts) {
    opts = opts || {};
    const card = window.CARDS[cardId];
    const S = window.UISilhouette;
    const ownArt = figureFor(opts.fighter);
    const foeArt = opts.opponent ? figureFor(opts.opponent) : ownArt;
    const a = card.attack, d = card.defense;

    const classes = ['ccard'];
    if (opts.selected) classes.push('selected');
    if (opts.disabled) classes.push('disabled');
    if (opts.readonly) classes.push('readonly');
    // CYCLING (RULES.md §2) is a THIRD state on this tile and not a shade of the other two, which
    // is the whole reason it is a class here rather than a caller's inline style. A selected card
    // is coming with you; a cycling card is leaving; a plain one is staying in your hand. Those
    // are three different futures and the rack has to say which is which at a glance -- so the
    // treatments are opposites (raised and accented against sunken and dashed) rather than two
    // brightnesses of the same idea.
    if (opts.cycling) classes.push('cycling');
    // THE CARD SAYS IT IS CARRYING RULES; nothing above it has to know. `rules: true` was built for
    // the Select tray and its whole appearance -- the grid area the block sits in AND the styling of
    // the ATK/DEF key -- was written under `.select-slots`. So the first surface to reuse the option
    // (the Cards page's detail sheet) got a rules block with no grid area, which auto-placed into an
    // implicit column and pushed the stat stack 36px outside the card's own border, and no styling,
    // which rendered "ATKIf guarded, shift 1 Flow... 1.DEFOn a full guard". Both were reported as
    // "it looks terrible", correctly. The layout belongs to the card that opted in.
    if (opts.rules) classes.push('has-rules');

    // Openings follow the same body the art does — the Attack half draws the opponent so it marks
    // THEIR exposure, the Defense half draws the owner so it marks the reader's own. On the Select
    // rack that is the point: five tiles, and the two that can reach a marked band are visible
    // before any of them is opened.
    const openFor = (role) => {
      const body = role === 'attack' ? opts.opponent : opts.fighter;
      return (body && window.ENGINE && window.ENGINE.openingsOn)
        ? window.ENGINE.openingsOn(body, role === 'attack' ? card : null) : [];
    };
    const sil = (role) => S
      ? S.render({ art: role === 'attack' ? foeArt : ownArt, role,
                   zones: S.zonesFor(card, role, null), name: card.name,
                   openings: openFor(role) })
      : '';
    const speed = window.ENGINE.printedSpeedOf(card);

    return `
      <button type="button" ${opts.id ? `id="${opts.id}"` : ''} class="${classes.join(' ')}"
        style="--frame:${ownershipColor(card)}" title="${escapeHtml(card.id)}"
        ${opts.disabled ? 'disabled' : ''} ${opts.readonly ? 'tabindex="-1" aria-disabled="true"'
          : (opts.tabindex != null ? `tabindex="${opts.tabindex}"` : '')}
        data-card-id="${cardId}"${opts.onclick ? ` onclick="${opts.onclick}"` : ''}>
        <div class="cc-head">
          ${styleSigilHtml(card)}
          <span class="ribbon-tag" style="--frame:${ownershipColor(card)}">${escapeHtml(ownershipLabel(card))}</span>
          ${card.dirty ? '<span class="ribbon-tag dirty">Dirty</span>' : ''}
          ${opts.cycling ? '<span class="cc-cycle-mark" aria-hidden="true">✕</span>' : ''}
        </div>
        <div class="cc-title">
          <span class="cc-name">${escapeHtml(card.name)}</span>
          ${opts.stateText ? `<span class="ribbon-state ${opts.stateClass || 'neutral'}">${opts.stateText}</span>` : ''}
        </div>
        <!-- NO cc-body WRAPPER. The figures and the numbers used to sit inside a flex box that put
             them side by side, which is right in a 158px rack column and is what made the card
             unable to lay out any other way: as a grid item that wrapper's max-content is the whole
             card, so at Telegraph width it swallowed the row and everything else wrapped behind it.
             Four CSS attempts failed on that and the fifth deleted it. Both parts are direct
             children now and every arrangement is a grid template on the card itself. -->
        <div class="cc-sils">
          <span class="cc-sil" data-role="attack"><span class="cc-sil-key">ATK${mechBadges(card, 'attack')}</span>${sil('attack')}</span>
          <span class="cc-sil" data-role="defense"><span class="cc-sil-key">DEF${mechBadges(card, 'defense')}</span>${sil('defense')}</span>
        </div>
        <div class="cc-nums">
          ${speedMeterHtml(card)}
          ${rangeBadgeRow(a.optimalRanges, opts.currentRange)}
          <span class="cc-num atk" title="Attack Power">${POWER_BURST}${attackPowerHtml(a)}<span>PWR</span></span>
          <span class="cc-num def" title="Defense Guard">${GUARD_SHIELD}<b>${d.guard}</b><span>GRD</span></span>
          ${compactCostHtml(card)}
        </div>
        ${opts.moveArt ? techniqueWindowHtml(card, opts) : moveGlossHtml(card)}
        ${opts.rules ? cardRulesHtml(card) : ''}
      </button>`;
  }

  // The whole moved set is exported, not just the two the deck builder calls. ui.js re-binds every
  // one of these by name so its ~120 existing call sites read exactly as they did before the move,
  // and a partial export would have forced those call sites to change -- which is the one thing a
  // pure-motion commit must not do.
  window.CardTile = {
    compactCardHtml, compactRowHtml, escapeHtml,
    ownershipColor, ownershipLabel, tierLabel, styleSigilHtml, moveGlossHtml, rangeBadgeRow, figureFor,
    speedMeterHtml, attackPowerHtml, compactCostHtml, conditionsText, cardRulesHtml,
    techniqueWindowHtml,
    POWER_BURST, GUARD_SHIELD
  };
})();
